import api from '@shared/services/api';
import {
  CreateCategoryRequest,
  QuestionCategory,
  UpdateCategoryRequest,
} from '../types/question-bank.types';

/**
 * Service for managing question categories
 */
export class CategoryService {
  private readonly BASE_URL = `/question-bank/categories`;

  /**
   * Get category hierarchy
   * @param includeQuestionCount Whether to include question counts
   */
  async getCategoryHierarchy(includeQuestionCount = false, questionType: 'tutorial' | 'question' = 'question'): Promise<QuestionCategory[]> {
    try {
      const response = await api.get(`${this.BASE_URL}/hierarchy/all`, {
        params: { includeQuestionCount, questionType }
      });
      return response.data.hierarchy;
    } catch (error) {
      console.error('Error fetching category hierarchy:', error);
      throw error;
    }
  }

  /**
   * Get category by ID
   */
  async getCategoryById(id: string): Promise<QuestionCategory> {
    try {
      const response = await api.get(`${this.BASE_URL}/${id}`);
      return response.data.category;
    } catch (error) {
      console.error(`Error fetching category with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create new category
   */
  async createCategory(data: CreateCategoryRequest): Promise<QuestionCategory> {
    try {
      const response = await api.post(this.BASE_URL, data);
      return response.data.category;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  /**
   * Update category
   */
  async updateCategory(
    id: string,
    data: UpdateCategoryRequest
  ): Promise<QuestionCategory> {
    try {
      const response = await api.put(`${this.BASE_URL}/${id}`, data);
      return response.data.category;
    } catch (error) {
      console.error(`Error updating category with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete category
   */
  async deleteCategory(id: string): Promise<void> {
    try {
      await api.delete(`${this.BASE_URL}/${id}`);
    } catch (error) {
      console.error(`Error deleting category with ID ${id}:`, error);
      throw error;
    }
  }

  getCategoryPath(
    category: QuestionCategory,
    categories: QuestionCategory[]
  ): string {
    if (!category.parentId) {
      return category.name;
    }

    // Buscar en toda la jerarquía, no solo en el primer nivel
    const parent = this.findCategoryById(category.parentId, categories);
    if (!parent) {
      return category.name;
    }

    return `${this.getCategoryPath(parent, categories)} > ${category.name}`;
  }

  /**
   * Encuentra una categoría por ID en toda la jerarquía
   */
  findCategoryById(
    id: string,
    categories: QuestionCategory[]
  ): QuestionCategory | null {
    for (const cat of categories) {
      if (cat.id === id) return cat;
      if (cat.subcategories && cat.subcategories.length > 0) {
        const found = this.findCategoryById(id, cat.subcategories);
        if (found) return found;
      }
    }
    return null;
  }
}
